// 1:1 port of prototype/components/core/Button.jsx (design D3): same name, same
// prop contract (prototype/components/core/Button.d.ts), styled only from token
// CSS vars. Verbatim except for TypeScript typing.

import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

/**
 * Extends the native button attributes (UI/UX audit **F-02** and **F-03**). Two reasons, both
 * load-bearing:
 *
 *   * **`type` must be settable, and must default to `"button"`.** A `<button>` with no `type` inside
 *     a `<form>` is a *submit* button per the HTML default, so once F-02 wraps Home's join group in a
 *     form, every unmarked button inside it — **Paste** included — would submit it. Defaulting to
 *     `"button"` makes submission opt-in: only a control explicitly marked `type="submit"` submits.
 *   * **`aria-*` and the rest of the native surface** were previously unreachable, which is why
 *     icon-only controls elsewhere were hand-rolled as raw `<button>`s rather than using this one —
 *     the unstyled ✓ of F-03 being the visible cost.
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style. Default 'primary'. */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /**
   * 'md' (44px), 'lg' (52px), or 'icon' — a square 44×44 control with **no horizontal padding**.
   *
   * `icon` exists because 'md' carries `padding: 0 20px`, so a single-glyph label renders a box 40px
   * wider than the 44×44 an icon control wants. It also pins `width`/`height` and sets
   * `box-sizing: border-box`, so 44px is the *outer* box even with a border — the kit styles inline
   * from tokens and cannot assume a global border-box reset.
   */
  size?: 'md' | 'lg' | 'icon';
  /** Optional leading icon node (16-20px). */
  icon?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}

const VARIANTS: Record<NonNullable<ButtonProps['variant']>, CSSProperties> = {
  primary: { background: 'var(--accent-strong)', color: '#FFFDF6', border: '1px solid var(--accent-strong)' },
  secondary: { background: 'var(--surface-card)', color: 'var(--ink-1)', border: '1px solid var(--line-strong)' },
  ghost: { background: 'transparent', color: 'var(--accent-strong)', border: '1px solid transparent' },
  danger: { background: 'var(--danger)', color: '#FFFDF6', border: '1px solid #A33823' },
};

/** Per-size geometry. `icon` is square and unpadded; the other two keep their original values. */
const SIZES: Record<NonNullable<ButtonProps['size']>, CSSProperties> = {
  md: { fontSize: 15, minHeight: 'var(--hit-target)', padding: '0 20px' },
  lg: { fontSize: 17, minHeight: 52, padding: '0 28px' },
  icon: {
    fontSize: 15,
    padding: 0,
    width: 'var(--hit-target)',
    height: 'var(--hit-target)',
    minWidth: 'var(--hit-target)',
    minHeight: 'var(--hit-target)',
    flex: 'none',
  },
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  children,
  disabled,
  style,
  // Defaulted, never inherited from the HTML default — see the `ButtonProps` note.
  type = 'button',
  ...rest
}: ButtonProps) {
  const v = VARIANTS[variant] ?? VARIANTS.primary;
  const s = SIZES[size] ?? SIZES.md;
  return (
    <button
      {...rest}
      data-ui="button"
      type={type}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // The 44px minimum is an *outer* box in every size, so a border can never eat into it.
        boxSizing: 'border-box',
        gap: 8,
        fontFamily: 'var(--font-display)',
        fontWeight: 500,
        borderRadius: 'var(--r-pill)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        ...s,
        ...v,
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}
